import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Languages, Users, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { ASSETS } from "../lib/assets";
import { Reveal, MaskedLine, GoldLine, Eyebrow } from "../components/Reveal";

export default function Academics() {
  const { t } = useLang();
  const ac = t.academics;
  const icons = [BookOpen, Languages, Users];

  return (
    <>
      <section className="bg-ivory pb-16 pt-36 lg:pt-44">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <MaskedLine>
            <h1 className="font-heading text-5xl font-semibold leading-none text-navy sm:text-6xl lg:text-7xl">{ac.title}</h1>
          </MaskedLine>
          <GoldLine className="mt-8 w-20" delay={0.3} />
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-2xl text-base font-light leading-relaxed text-ink/70 md:text-lg"
          >
            {ac.sub}
          </motion.p>
        </div>
      </section>

      <section className="bg-ivory pb-24 lg:pb-32" data-testid="academics-pillars">
        <div className="mx-auto grid max-w-7xl gap-px bg-gold/30 px-5 sm:grid-cols-3 sm:px-8">
          {ac.pillars.map((p, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={i} delay={i * 0.12} className="group bg-cream p-8 transition-colors duration-500 hover:bg-warmneutral lg:p-10">
                <Icon size={26} strokeWidth={1.4} className="text-gold" />
                <h3 className="mt-6 font-heading text-2xl font-semibold text-navy md:text-3xl">{p.title}</h3>
                <span className="mt-4 block h-px w-8 bg-gold transition-[width] duration-500 group-hover:w-16" aria-hidden="true" />
                <p className="mt-5 text-sm font-light leading-relaxed text-ink/70">{p.body}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-navy-deep py-24 text-ivory lg:py-32" data-testid="academics-journey">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-4">
            <Eyebrow light>{ac.journeyEyebrow}</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-semibold leading-tight md:text-5xl">{ac.journeyTitle}</h2>
            <p className="mt-6 max-w-sm text-base font-light leading-relaxed text-ivory/70 md:text-lg">{ac.journeyIntro}</p>
          </Reveal>
          <div className="min-w-0 lg:col-span-8">
            {t.journey.map((j, i) => (
              <Reveal key={i} delay={i * 0.08} y={18} data-testid={`journey-stage-${i}`}>
                <div className="group grid grid-cols-12 items-baseline gap-4 border-b border-ivory/10 py-6 transition-colors duration-300 hover:border-gold/60">
                  <span className="col-span-2 font-heading text-2xl font-medium text-gold/60">0{i + 1}</span>
                  <span className="col-span-6 min-w-0 break-words font-heading text-2xl font-medium md:text-3xl">{j.stage}</span>
                  <span className={`col-span-4 min-w-0 break-words text-right text-xs uppercase tracking-[0.2em] md:text-sm ${j.soon ? "text-gold" : "text-ivory/50"}`}>
                    {j.level}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 lg:py-28" data-testid="academics-note">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-6">
            <div className="relative">
              <span className="absolute -left-3 -top-3 h-full w-full border border-gold/50" aria-hidden="true" />
              <img src={ASSETS.classroom} alt="SMHSS classroom" className="relative h-72 w-full object-cover sm:h-96" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="min-w-0 lg:col-span-6">
            <p className="max-w-lg font-heading text-2xl font-medium leading-snug text-navy md:text-3xl">{ac.note}</p>
            <Link
              to="/contact"
              data-testid="academics-contact-cta"
              className="group mt-10 inline-flex items-center gap-3 bg-navy px-7 py-3.5 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-navy-soft"
            >
              {ac.noteCta}
              <ArrowRight size={16} className="text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
